import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/consumer/lib/api'
import type { PaginatedResponse } from '@/shared/types/api'
import type {
  ProductFilterOption,
  ProductFilterFacet,
  ProductFilterValue,
} from '@/shared/components/ui/product-filter-menu'
import type { ValidationRule } from '@/shared/lib/forms'

export interface ProductSearchResult {
  uuid: string
  cached_short_name: string
  cached_long_name: string
  product_type: string
  image: { uuid: string; file_url: string; thumbnail_url: string | null } | null
  product_template: { uuid: string; name: string }
  /** Sizes under this product line. Only annotated in `group=lines` mode; a
   *  `VARIANT_BASE` with 0 is not registrable and must not be offered. */
  variant_count?: number
  /** Set on `VARIANT` rows only - the line this size belongs to. */
  parent_product_uuid?: string | null
  parent_product_name?: string | null
}

export interface FieldMeta {
  uuid: string
  code_name: string
  display_name: string
  description: string
  field_type: string
  attribute_level: string
  required: boolean
  required_to_save: boolean
  required_to_publish: boolean
  visibility: string
  hint: string
  options: { value: string; label: string }[]
  validation_rules: ValidationRule[]
  order: number
}

export interface WarrantyTemplateOption {
  uuid: string
  duration_in_months: number | null
  is_lifetime: boolean
  display_name?: string
}

export interface WarrantyTemplate {
  uuid: string
  name: string
  warranty_type: string
  pre_assigned_option_uuid?: string
  options: WarrantyTemplateOption[]
}

export interface ConsumerFieldMeta {
  code_name: string
  display_name: string
  description: string
  field_type: string
  required: boolean
  hint: string
  configuration_data: Record<string, unknown>
  order: number
}

export interface TransactionAttributeMeta {
  uuid: string
  code_name: string
  display_name: string
  description: string
  field_type: string
  required: boolean
  order: number
  visibility: string
  configuration_data: Record<string, unknown>
  hint: string | null
}

export interface TransactionTemplate {
  uuid: string
  name: string
  slug: string
  description: string
  icon: string
  seed_kind: string
  attributes: TransactionAttributeMeta[]
}

export interface ProductAttributeValue {
  code_name: string
  display_name: string
  field_type: string
  attribute_level: string
  value: unknown
}

export interface RegistrationMetadata {
  product: ProductSearchResult
  product_attributes: ProductAttributeValue[]
  fields: FieldMeta[]
  consumer_fields: ConsumerFieldMeta[]
  /** Returning consumer's saved attribute values (e.g. address), for prefill. */
  consumer_attribute_values?: Record<string, unknown>
  /** Consumer's current phone number, if any. */
  consumer_phone?: string
  /** True when the consumer has no phone on file - collect it in the form. */
  capture_phone?: boolean
  transaction_template: TransactionTemplate | null
  /** Host (vehicle / property) fields the product's template captures. */
  host_template?: { name: string; attributes: TransactionAttributeMeta[] } | null
  warranty_templates: WarrantyTemplate[]
}

export interface WarrantySummary {
  uuid: string
  class_name: string
  option_display_name: string
  active_from: string
  expires_at: string | null
  status: string
}

export interface RegistrationResponse {
  holding: {
    uuid: string
    product: { uuid: string; cached_short_name: string; cached_long_name: string }
    consumer_account: { uuid: string; email: string; first_name: string; last_name: string }
    attribute_values?: { code_name: string; display_name: string; field_type: string; value: unknown }[]
    created_at: string
  }
  warranty: WarrantySummary | null
  warnings: string[]
}

// Canonical filter types live with the shared ProductFilterMenu (imported at
// top); re-export so existing consumer imports keep working.
export type { ProductFilterOption, ProductFilterFacet, ProductFilterValue }

/** Serialise the selected-filter map into repeatable comma-joined params. */
function filterParams(filters: ProductFilterValue): Record<string, string> {
  const params: Record<string, string> = {}
  for (const [key, values] of Object.entries(filters)) {
    if (values.length) params[key] = values.join(',')
  }
  return params
}

export function useConsumerProductFilters() {
  return useQuery<{ facets: ProductFilterFacet[] }>({
    queryKey: ['consumer-product-filters'],
    queryFn: () => api.get('/products/filters/').then((r) => r.data),
  })
}

/** In-use product category for the grouped category-browse view. */
export interface ConsumerProductCategory {
  slug: string
  name: string
  icon: string
  product_count: number
}

export function useConsumerProductCategories() {
  return useQuery<{ categories: ConsumerProductCategory[] }>({
    queryKey: ['consumer-product-categories'],
    queryFn: () => api.get('/products/categories/').then((r) => r.data),
  })
}

export function useConsumerProductSearch(
  query: string,
  page: number,
  pageSize: number,
  filters: ProductFilterValue = {},
) {
  return useQuery<PaginatedResponse<ProductSearchResult>>({
    queryKey: ['consumer-product-search', query, page, pageSize, filters],
    queryFn: () =>
      api
        .get('/products/search/', {
          params: { q: query, page, page_size: pageSize, ...filterParams(filters) },
        })
        .then((r) => r.data),
    // An empty query returns the browsable catalogue, so the hook is always
    // enabled: the listing shows on mount without the consumer typing, and
    // typing filters it. This is what lets few-product tenants skip searching.
    // Keep the previous page visible while the next loads (no blank flash).
    placeholderData: (prev) => prev,
  })
}

/**
 * The tenant's product LINES for the register step (`?group=lines`).
 *
 * One row per line (`VARIANT_BASE`) carrying `variant_count`, plus standalone
 * products and any published size whose line is unpublished - so grouping never
 * hides something a consumer can register. Browse only: no query, no filters,
 * because a search is a "find this product" action that wants the flat feed.
 */
export function useConsumerProductLines(
  page: number,
  pageSize: number,
  enabled = true,
) {
  return useQuery<PaginatedResponse<ProductSearchResult>>({
    queryKey: ['consumer-product-lines', page, pageSize],
    queryFn: () =>
      api
        .get('/products/search/', {
          params: { group: 'lines', page, page_size: pageSize },
        })
        .then((r) => r.data),
    enabled,
    // Keep the current page on screen while the next loads (no blank flash).
    placeholderData: (prev) => prev,
  })
}

/** The sizes under one product line (`?parent=<uuid>`). */
export function useConsumerProductVariants(parentUuid: string | null) {
  return useQuery<PaginatedResponse<ProductSearchResult>>({
    queryKey: ['consumer-product-variants', parentUuid],
    queryFn: () =>
      api
        .get('/products/search/', { params: { parent: parentUuid, page_size: 100 } })
        .then((r) => r.data),
    enabled: !!parentUuid,
  })
}

export function useConsumerRegistrationMetadata(productUuid: string | null) {
  return useQuery<RegistrationMetadata>({
    queryKey: ['consumer-registration-metadata', productUuid],
    queryFn: () =>
      api.get(`/products/${productUuid}/registration-metadata/`).then((r) => r.data),
    enabled: !!productUuid,
  })
}

export function useConsumerFileUpload() {
  return useMutation<
    { uuid: string; file_url: string; thumbnail_url: string | null; file_name: string },
    Error,
    { file: File; use_case: string }
  >({
    mutationFn: ({ file, use_case }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('use_case', use_case)
      return api
        .post('/files/upload/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          // Large files on slow mobile uplinks need longer than the default
          // 60s; still finite so a dead upload eventually errors (never hangs).
          timeout: 120000,
        })
        .then((r) => r.data)
    },
  })
}

export function useConsumerRegister() {
  const qc = useQueryClient()
  return useMutation<
    RegistrationResponse,
    Error,
    {
      product_uuid: string
      attribute_values?: Record<string, unknown>
      consumer_attribute_values?: Record<string, unknown>
      transaction_data?: Record<string, unknown>
      warranty_template_option_uuid?: string
    }
  >({
    mutationFn: (data) => api.post('/registrations/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consumer-session'] }),
  })
}
